import type { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../api/client";
import { setImpersonationHeaders } from "../../../impersonation/impersonationHandler";
import {
  mapViewOnlyFileProtection,
  toProtectionProbe,
} from "../../../services/viewOnlyFileProtection/toProtectionProbe";
import { getAuthMode } from "../../../variables";
import { UserActions } from "./actions";
import type { UserPayload } from "./reducer";
import type { ProtectionProbe, UserPermissionsResponse, UserState } from "./types";
import { IdentityLoadingState } from "./types";

type UserDispatch = Dispatch<PayloadAction<UserPayload>>;

const getUserIdentity = withErrorHandling(api.user.loadIdentity);
const getUserPermissions = withErrorHandling(api.user.loadPermissions);

const toUserStatePayload = (userPermissions: UserPermissionsResponse): Partial<UserState> => {
  const { fp, ...rest } = userPermissions;
  return { ...rest, viewOnlyFileProtection: mapViewOnlyFileProtection(fp) };
};

export function refreshViewOnlyFileProtection(): (dispatch: UserDispatch) => Promise<ProtectionProbe> {
  return async (dispatch: UserDispatch): Promise<ProtectionProbe> => {
    const [userPermissions, error] = await getUserPermissions();

    if (error) {
      logError(error, "[refreshViewOnlyFileProtection] loadPermissions");
    }

    const probe = toProtectionProbe(userPermissions, error);
    if (probe.answered) {
      dispatch(UserActions.UpdateState({ viewOnlyFileProtection: probe.protection }));
    }

    return probe;
  };
}

export function loadLargeAvatar() {
  return async (dispatch: UserDispatch) => {
    dispatch(UserActions.UpdateLoading({ identity: true }));

    const [identity, error] = await getUserIdentity(true);

    if (error) {
      logError(error, "[loadLargeAvatar] loadIdentity");
    } else {
      dispatch(UserActions.UpdateIdentity({ ...identity, largeImage: true }));
    }

    dispatch(UserActions.UpdateLoading({ identity: false }));
  };
}

export function loadUserInfo(client: string | undefined) {
  return async (dispatch: UserDispatch) => {
    dispatch(UserActions.UpdateLoading({ identity: true, state: IdentityLoadingState.None }));

    const isImpersonation = getAuthMode() === "impersonation";

    const getErrorState = (error: ApiError): IdentityLoadingState => {
      if (error.code !== 401 && error.code !== 403) {
        return IdentityLoadingState.LoadingError;
      }
      return isImpersonation
        ? IdentityLoadingState.LoadingErrorImpersonationPermissions
        : IdentityLoadingState.AuthorizationOrAccessError;
    };

    if (isImpersonation) {
      const [userPermissions, userPermissionsError] = await getUserPermissions();
      if (userPermissionsError) {
        logError(userPermissionsError, "[loadUserInfo] loadPermissions");
        dispatch(UserActions.UpdateLoading({ identity: false, state: getErrorState(userPermissionsError) }));
        return;
      }

      const { email, impersonationTargetType } = userPermissions;
      if (client && email) {
        setImpersonationHeaders(client, email, impersonationTargetType ?? "Investor");
      }

      const [userIdentity, userIdentityError] = await getUserIdentity();
      if (userIdentityError) {
        logError(userIdentityError, "[loadUserInfo] loadIdentity");
        dispatch(UserActions.UpdateLoading({ identity: false, state: getErrorState(userIdentityError) }));
        return;
      }

      dispatch(UserActions.UpdateState({ ...toUserStatePayload(userPermissions), identity: { ...userIdentity } }));
      dispatch(UserActions.UpdateLoading({ identity: false, state: IdentityLoadingState.Loaded }));
      return;
    }

    const [permissionsResp, identityResp] = await Promise.all([getUserPermissions(), getUserIdentity()]);

    const [userPermissions, userPermissionsError] = permissionsResp;
    const [userIdentity, userIdentityError] = identityResp;
    const error = userPermissionsError || userIdentityError;
    if (error) {
      logError(error, "[loadUserInfo]");
      dispatch(UserActions.UpdateLoading({ identity: false, state: getErrorState(error) }));
      return;
    }

    dispatch(UserActions.UpdateState({ ...toUserStatePayload(userPermissions), identity: { ...userIdentity } }));
    dispatch(UserActions.UpdateLoading({ identity: false, state: IdentityLoadingState.Loaded }));
  };
}

export function reportClientSwitch(): (dispatch: UserDispatch) => Promise<void> {
  return async (dispatch: UserDispatch) => {
    // The backend records the opened portal from this call; loading state stays Loaded on purpose.
    const [userPermissions, error] = await getUserPermissions();

    if (error) {
      logError(error, "[reportClientSwitch] loadPermissions");
      return;
    }

    dispatch(UserActions.UpdateState(toUserStatePayload(userPermissions)));
  };
}
